// ============================================================
//  PROFILE STORE
// ============================================================

import { create } from "zustand";
import { ref, set as fbSet, remove, onValue, off } from "firebase/database";
import { db } from "../firebase/config";
import type { Profile } from "../types";

const LOCAL_KEY = "fleetops_profiles";

function loadLocal(): Profile[] {
  try {
    return JSON.parse(localStorage.getItem(LOCAL_KEY) || "[]");
  } catch {
    return [];
  }
}

function saveLocal(profiles: Profile[]) {
  localStorage.setItem(LOCAL_KEY, JSON.stringify(profiles));
}

interface ProfileState {
  profiles: Profile[];

  addProfile: (p: Profile) => void;
  updateProfile: (id: string, updater: (p: Profile) => Profile) => void;
  removeProfile: (id: string) => void;
  getById: (id: string) => Profile | undefined;

  startSync: () => () => void;
}

export const useProfileStore = create<ProfileState>((set, get) => ({
  profiles: loadLocal(),

  addProfile(p) {
    const next = [p, ...get().profiles];
    saveLocal(next);
    set({ profiles: next });
    fbSet(ref(db, `profiles/${p.id}`), p);
  },

  updateProfile(id, updater) {
    const profiles = get().profiles.map((p) =>
      p.id === id ? updater(JSON.parse(JSON.stringify(p))) : p,
    );
    saveLocal(profiles);
    set({ profiles });
    const updated = profiles.find((p) => p.id === id);
    if (updated) fbSet(ref(db, `profiles/${id}`), updated);
  },

  removeProfile(id) {
    const profiles = get().profiles.filter((p) => p.id !== id);
    saveLocal(profiles);
    set({ profiles });
    remove(ref(db, `profiles/${id}`));
  },

  getById(id) {
    return get().profiles.find((p) => p.id === id);
  },

  startSync() {
    const profilesRef = ref(db, "profiles");
    const unsub = onValue(profilesRef, (snap) => {
      const raw = snap.val();
      const profiles: Profile[] = raw ? Object.values(raw) : [];
      saveLocal(profiles);
      set({ profiles });
    });
    return () => unsub();
  },
}));
